import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { publicRequest } from "../api/requestMethods";
import reportClientError from "../utils/clientTelemetry";

// ─────────────────────────────────────────────────────────────────────────────
// BROWSE-TIME DELIVERY COVERAGE (web) — "can this vendor deliver to me?"
//
// Reads the AUTHORITATIVE backend answer (GET /api/coverage → the same
// vendor-coords + road-distance + radius logic the order-create 422 gate uses),
// so the early UX (shop-entry banner, add-to-cart block, cart pre-checkout gate)
// AGREES with the final gate. This replaces the client-side haversine
// DeliveryBadge math, which could say "Delivers" while the backend 422'd at
// checkout.
//
// The backend also returns the semantic delivery policy (for example NATIONAL),
// so presentation never has to infer coverage from an oversized numeric radius.
// Results are cached per (vendorId, rounded lat/lng) so revisiting a shop or
// re-rendering a product grid doesn't re-hit the endpoint.
// ─────────────────────────────────────────────────────────────────────────────

// Round coords to ~11 m so tiny GPS jitter doesn't bust the cache every render.
const roundCoord = (n) => (Number.isFinite(Number(n)) ? Number(n).toFixed(4) : "");
export const coverageKey = (vendorId, lat, lng) =>
  `${vendorId}:${roundCoord(lat)},${roundCoord(lng)}`;

// An outlet (branch) has its own coordinates and radius, so a check for one
// is keyed by the OUTLET id, the same key the batch results use for outlets,
// and the backend is told which outlet so it does not answer for the parent.
export const fetchCoverage = createAsyncThunk(
  "coverage/fetch",
  async ({ vendorId, outletId = null, lat, lng, vendorType }, { rejectWithValue }) => {
    const key = coverageKey(outletId || vendorId, lat, lng);
    try {
      const params = new URLSearchParams({ vendorId: String(vendorId) });
      if (outletId) params.set("outletId", String(outletId));
      if (lat != null && lng != null) {
        params.set("lat", String(lat));
        params.set("lng", String(lng));
      }
      if (vendorType) params.set("vendorType", vendorType);
      const res = await publicRequest.get(`/api/coverage?${params.toString()}`);
      return { key, data: res.data };
    } catch (err) {
      reportClientError(err, { area: "coverage_check", vendor_type: vendorType || "unknown" });
      return rejectWithValue({
        key,
        message: err?.response?.data?.message || err.message || "Coverage check failed",
      });
    }
  }
);

// ── Batch check (listings pages) ─────────────────────────────────────────────
// /restaurants-style pages show DOZENS of vendors; one POST /api/coverage/batch
// replaces N GETs (the backend screens obviously-out-of-range vendors for free
// and only road-measures the plausible ones). Results land under the SAME
// coverageKey entries the single-vendor useCoverage hook reads, so a batch on
// the listing pre-warms the shop-entry banner for whichever vendor the
// customer taps next.
//
// An entry is a bare vendor id (legacy) or `{ vendorId, outletId }` for an
// outlet card. The server keys an outlet's verdict by the OUTLET id, which is
// also the key the single-vendor hook reads for a branch, so a parent and its
// outlets can all sit in one batch without sharing a verdict.
export const batchEntryKey = (entry) =>
  entry && typeof entry === "object"
    ? String(entry.outletId || entry.vendorId)
    : String(entry);

// What goes on the wire for one entry: a plain id stays a plain id, an outlet
// entry names both ids (and the vendor type, which the server accepts per
// entry as well as at the top level).
const batchEntryPayload = (entry, vendorType) => {
  if (!entry || typeof entry !== "object") return String(entry);
  return {
    vendorId: String(entry.vendorId),
    ...(entry.outletId ? { outletId: String(entry.outletId) } : {}),
    ...(vendorType ? { vendorType } : {}),
  };
};

export const fetchCoverageBatch = createAsyncThunk(
  "coverage/fetchBatch",
  async ({ vendorIds, lat, lng, vendorType }, { rejectWithValue }) => {
    try {
      const res = await publicRequest.post(`/api/coverage/batch`, {
        vendorIds: vendorIds.map((entry) => batchEntryPayload(entry, vendorType)),
        lat,
        lng,
        ...(vendorType ? { vendorType } : {}),
      });
      return { vendorIds, lat, lng, results: res.data?.results || {} };
    } catch (err) {
      reportClientError(err, {
        area: "coverage_batch_check",
        vendor_type: vendorType || "unknown",
      });
      return rejectWithValue({
        vendorIds,
        lat,
        lng,
        message: err?.response?.data?.message || err.message || "Coverage check failed",
      });
    }
  }
);

const initialState = {
  // key → { status, deliverable, distanceKm, radius, reason, error }
  byKey: {},
};

const coverageSlice = createSlice({
  name: "coverage",
  initialState,
  reducers: {
    resetCoverage: () => initialState,
  },
  extraReducers(builder) {
    builder
      .addCase(fetchCoverage.pending, (state, action) => {
        const { vendorId, lat, lng } = action.meta.arg;
        const key = coverageKey(vendorId, lat, lng);
        state.byKey[key] = { ...(state.byKey[key] || {}), status: "loading", error: null };
      })
      .addCase(fetchCoverage.fulfilled, (state, action) => {
        const { key, data } = action.payload;
        state.byKey[key] = {
          status: "succeeded",
          deliverable: data?.deliverable ?? null,
          distanceKm: data?.distanceKm ?? null,
          radius: data?.radius ?? null,
          deliveryCoverage: data?.deliveryCoverage ?? null,
          coverageCountry: data?.coverageCountry ?? null,
          reason: data?.reason ?? null,
          error: null,
        };
      })
      .addCase(fetchCoverage.rejected, (state, action) => {
        const key = action.payload?.key || coverageKey(
          action.meta.arg.vendorId, action.meta.arg.lat, action.meta.arg.lng
        );
        state.byKey[key] = {
          status: "failed",
          deliverable: null,
          distanceKm: null,
          radius: null,
          deliveryCoverage: null,
          coverageCountry: null,
          reason: null,
          error: action.payload?.message || "Coverage check failed",
        };
      })
      .addCase(fetchCoverageBatch.pending, (state, action) => {
        const { vendorIds = [], lat, lng } = action.meta.arg;
        vendorIds.forEach((entry) => {
          const key = coverageKey(batchEntryKey(entry), lat, lng);
          // Don't clobber an entry the single-vendor path already resolved.
          if (state.byKey[key]?.status === "succeeded") return;
          state.byKey[key] = { ...(state.byKey[key] || {}), status: "loading", error: null };
        });
      })
      .addCase(fetchCoverageBatch.fulfilled, (state, action) => {
        const { vendorIds = [], lat, lng, results = {} } = action.payload;
        vendorIds.forEach((entry) => {
          const id = batchEntryKey(entry);
          const key = coverageKey(id, lat, lng);
          const data = results[id];
          if (!data) {
            // Backend skipped/deduped this id — mark failed so the UI degrades
            // to its client-side estimate instead of spinning forever.
            state.byKey[key] = {
              status: "failed", deliverable: null, distanceKm: null,
              radius: null, deliveryCoverage: null, coverageCountry: null,
              reason: null, error: "No verdict returned",
            };
            return;
          }
          state.byKey[key] = {
            status: "succeeded",
            deliverable: data.deliverable ?? null,
            distanceKm: data.distanceKm ?? null,
            radius: data.radius ?? null,
            deliveryCoverage: data.deliveryCoverage ?? null,
            coverageCountry: data.coverageCountry ?? null,
            reason: data.reason ?? null,
            error: null,
          };
        });
      })
      .addCase(fetchCoverageBatch.rejected, (state, action) => {
        const { vendorIds = [], lat, lng } = action.meta.arg;
        vendorIds.forEach((entry) => {
          const key = coverageKey(batchEntryKey(entry), lat, lng);
          if (state.byKey[key]?.status === "succeeded") return;
          state.byKey[key] = {
            status: "failed",
            deliverable: null,
            distanceKm: null,
            radius: null,
            deliveryCoverage: null,
            coverageCountry: null,
            reason: null,
            error: action.payload?.message || "Coverage check failed",
          };
        });
      });
  },
});

export const { resetCoverage } = coverageSlice.actions;
export default coverageSlice.reducer;
